import Button from '@rio-cloud/rio-uikit/Button';
import DayPicker from '@rio-cloud/rio-uikit/DayPicker';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { BookingOverviewFilters } from './bookingOverviewFilters';

const inputId = 'bookings-contract-end-date-range';

function CalendarActions({ close, clear }: { close: () => void; clear: () => void }) {
    const footer = useRef<HTMLDivElement>(null);
    const closeAndRestoreFocus = () => {
        document.getElementById(inputId)?.focus();
        close();
    };

    useEffect(() => {
        // UIKIT 3 handles Escape on its trigger, but not from a focused calendar day.
        const handleEscape = (event: KeyboardEvent) => {
            if (
                event.key === 'Escape' &&
                event.target instanceof Node &&
                footer.current?.closest('[role="dialog"]')?.contains(event.target)
            ) {
                document.getElementById(inputId)?.focus();
                close();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [close]);

    return (
        <div ref={footer} className='display-flex justify-content-between gap-10'>
            <Button
                onClick={() => {
                    clear();
                    closeAndRestoreFocus();
                }}
            >
                <FormattedMessage id='intl-msg:marketplace.bookings.overview.toolbar.clearDateRange' />
            </Button>
            <Button onClick={closeAndRestoreFocus}>
                <FormattedMessage id='intl-msg:marketplace.contracts.onboarding.done' />
            </Button>
        </div>
    );
}

export function BookingOverviewDateRange({
    filters,
    onChange,
}: {
    filters: BookingOverviewFilters;
    onChange: (filters: BookingOverviewFilters) => void;
}) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: 'intl-msg:marketplace.bookings.overview.toolbar.dateRange' });
    const dateRange = useMemo(
        () =>
            filters.endsFrom || filters.endsTo
                ? {
                      from: filters.endsFrom ? parseISO(filters.endsFrom) : undefined,
                      to: filters.endsTo ? parseISO(filters.endsTo) : undefined,
                  }
                : undefined,
        [filters.endsFrom, filters.endsTo]
    );

    return (
        <DayPicker
            id={inputId}
            mode='range'
            locale={intl.locale}
            value={dateRange}
            onChange={range =>
                onChange({
                    ...filters,
                    endsFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                    endsTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
            }
            className='margin-bottom-0 width-200'
            placeholder={label}
            inputProps={{ 'aria-label': label }}
            closeOnSelect={false}
            numberOfMonths={2}
            isClearable
            footer={({ close, clear }) => <CalendarActions close={close} clear={clear} />}
        />
    );
}
